import { useEffect, useState } from "react";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import { Layers, Pencil, Check, X } from "lucide-react";
import PipelineMultiSelector from "./PipelineMultiSelector";
import PipelineStageEditor from "./PipelineStageEditor";
import { useSettings } from "../../../context/SettingsContext";
import PageTitle from "../../../components/PageTitle";

// Post-Consultation Pipeline Statuses
const POST_CONSULT_STATUSES = [
  "post_consult",
  "proposal_shared",
  "negotiation",
  "treatment_booked",
  "won",
  "dropped",
];

export default function PostConsultPipelineStatus() {
  const { pipeline_status, savePipeline } = useSettings();

  const [editing, setEditing] = useState(false);
  const [stages, setStages] = useState([]);
  const [savedStages, setSavedStages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Filter pipeline_status to get only post-consult statuses
    const postConsultStages = (pipeline_status || []).filter((status) =>
      POST_CONSULT_STATUSES.includes(status)
    );
    setStages(postConsultStages);
    setSavedStages(postConsultStages);
  }, [pipeline_status]);

  const save = async () => {
    try {
      setSaving(true);
      // Merge post-consult stages with existing pipeline statuses
      const otherPipelineStages = (pipeline_status || []).filter(
        (status) => !POST_CONSULT_STATUSES.includes(status)
      );
      const updatedPipelineStatus = [...stages, ...otherPipelineStages];
      await savePipeline(updatedPipelineStatus);
      setSavedStages(stages);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setStages(savedStages);
    setEditing(false);
  };

  return (
    <Box elevation={0}>
      <Stack direction="row" justifyContent="space-between" mb={4}>
        <PageTitle parts={["Settings", "Post-Consult Pipeline", "Stages"]} />

        {!editing ? (
          <IconButton
            onClick={() => setEditing(true)}
            sx={{
              bgcolor: "#7C3AED",
              color: "white",
              borderRadius: 999,
              px: 2,
              py: 1,
              "&:hover": {
                bgcolor: "#6D28D9",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(124,58,237,0.45)",
              },
            }}
          >
            <Pencil size={16} />
          </IconButton>
        ) : (
          <Stack direction="row" spacing={1}>
            <IconButton
              disabled={saving}
              onClick={save}
              sx={{
                bgcolor: "#16A34A",
                color: "white",
                borderRadius: 3,
                px: 2,
                py: 1,
                "&:hover": {
                  bgcolor: "#15803D",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 16px rgba(22,163,74,0.45)",
                },
              }}
            >
              <Check size={16} />
            </IconButton>

            <IconButton
              disabled={saving}
              onClick={cancel}
              sx={{
                bgcolor: "rgba(220,38,38,0.15)",
                color: "#DC2626",
                borderRadius: 3,
                px: 2,
                py: 1,
                "&:hover": {
                  bgcolor: "rgba(220,38,38,0.25)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 16px rgba(220,38,38,0.35)",
                },
              }}
            >
              <X size={16} />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 4,
        }}
      >
        <PipelineMultiSelector
          title="Post-Consult Pipeline Stages"
          icon={Layers}
          selected={stages}
          setSelected={setStages}
          editing={editing}
          filterStatuses={POST_CONSULT_STATUSES}
        />

        <PipelineStageEditor
          flow={stages}
          setFlow={setStages}
          editing={editing}
        />
      </Box>
    </Box>
  );
}
